import { useEffect, useId, useRef, useState } from 'react';
import { getFieldSelection, insertTextIntoField, setFieldText, wrapFieldSelection } from 'text-field-edit';
import { Button } from '@/components/Button.tsx';
import { BoldIcon, LinkIcon, ListBulletIcon, MessageIcon } from '@/components/icons.tsx';
import styles from './DoctorSnippetFormDialog.module.css';

const TITLE_MAX = 160;
const BODY_MAX = 2000;

type Mode = 'create' | 'edit';

interface DoctorSnippetFormDialogProps {
  open: boolean;
  mode: Mode;
  scope: 'global' | 'org' | 'user';
  initialTitle?: string;
  initialBody?: string;
  initialAutomatic?: boolean;
  submitting?: boolean;
  serverError?: string | null;
  onSubmit: (title: string, body: string, automatic: boolean) => void;
  onClose: () => void;
}

export function DoctorSnippetFormDialog({
  open,
  mode,
  scope,
  initialTitle = '',
  initialBody = '',
  initialAutomatic = false,
  submitting = false,
  serverError,
  onSubmit,
  onClose,
}: DoctorSnippetFormDialogProps) {
  const headingId = useId();
  const [title, setTitle] = useState(initialTitle);
  const [text, setText] = useState(initialBody);
  const [automatic, setAutomatic] = useState(initialAutomatic);
  const [submitted, setSubmitted] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(initialTitle);
    setText(initialBody);
    setAutomatic(initialAutomatic);
    setSubmitted(false);
    queueMicrotask(() => firstRef.current?.focus());
  }, [open, initialTitle, initialBody, initialAutomatic, mode]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const titleLen = title.length;
  const bodyLen = text.length;
  const titleError = title.trim().length === 0 ? 'Titel is verplicht.' : titleLen > TITLE_MAX ? `Max. ${TITLE_MAX} tekens.` : null;
  const textError = text.trim().length === 0 ? 'Tekst is verplicht.' : bodyLen > BODY_MAX ? `Max. ${BODY_MAX} tekens.` : null;
  const hasClientError = !!(titleError || textError);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (hasClientError || submitting) return;
    onSubmit(title.trim(), text, automatic);
  }

  function applyBold() {
    const field = bodyRef.current;
    if (!field) return;
    wrapFieldSelection(field, '**');
    field.focus();
  }

  function applyLink() {
    const field = bodyRef.current;
    if (!field) return;
    const url = window.prompt('Naar welke URL moet de link verwijzen?', 'https://');
    if (!url) return;
    if (getFieldSelection(field)) {
      wrapFieldSelection(field, '[', `](${url})`);
    } else {
      insertTextIntoField(field, `[tekst](${url})`);
    }
    field.focus();
  }

  function applyBullet() {
    const field = bodyRef.current;
    if (!field) return;
    const { selectionStart, selectionEnd, value } = field;
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const lineEndSearch = value.indexOf('\n', selectionEnd);
    const lineEnd = lineEndSearch === -1 ? value.length : lineEndSearch;
    const block = value.slice(lineStart, lineEnd);
    const prefixed = block.split('\n').map((l) => (l.startsWith('- ') ? l : `- ${l}`)).join('\n');
    setFieldText(field, value.slice(0, lineStart) + prefixed + value.slice(lineEnd));
    field.focus();
  }

  const heading = mode === 'create' ? 'Snippet toevoegen' : 'Eigen tekst bewerken';

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby={headingId}>
      <div className={styles.panel}>
        <div className="icon-wrap icon-wrap-primary"><MessageIcon size={24} /></div>
        <h2 id={headingId} className={styles.title}>{heading}</h2>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label className="field-label" htmlFor="snippet-title">Titel</label>
            <input ref={firstRef} id="snippet-title" className={submitted && titleError ? 'field-input field-input-error' : 'field-input'} type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={TITLE_MAX} autoComplete="off" disabled={submitting} />
            <span className={styles.fieldError}>{(submitted && titleError) || ' '}</span>
            <span className={styles.counter}>{titleLen}/{TITLE_MAX}</span>
          </div>
          <div className={styles.field}>
            <label className="field-label" htmlFor="snippet-body">Tekst</label>
            <div className={styles.toolbar}>
              <button type="button" className={styles.toolbarBtn} title="Vet" aria-label="Vet" onClick={applyBold} disabled={submitting}><BoldIcon size={14} /></button>
              <button type="button" className={styles.toolbarBtn} title="Link invoegen" aria-label="Link invoegen" onClick={applyLink} disabled={submitting}><LinkIcon size={14} /></button>
              <button type="button" className={styles.toolbarBtn} title="Opsomming" aria-label="Opsomming" onClick={applyBullet} disabled={submitting}><ListBulletIcon size={14} /></button>
            </div>
            <textarea ref={bodyRef} id="snippet-body" className={submitted && textError ? 'field-input field-input-error' : 'field-input'} rows={12} value={text} onChange={(e) => setText(e.target.value)} maxLength={BODY_MAX} disabled={submitting} />
            <span className={styles.fieldError}>{(submitted && textError) || ' '}</span>
            <span className={styles.counter}>{bodyLen}/{BODY_MAX}</span>
          </div>
          {scope === 'user' && (
            <label className={styles.automaticCheck}>
              <input type="checkbox" checked={automatic} onChange={(e) => setAutomatic(e.target.checked)} disabled={submitting} />
              <span>Automatisch versturen bij een nieuw gesprek. Er kan maar 1 automatische snippet zijn.</span>
            </label>
          )}
          {serverError && <p className={styles.serverError} role="alert">{serverError}</p>}
          <div className={styles.actions}>
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>Annuleren</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Bezig…' : mode === 'create' ? 'Toevoegen' : 'Opslaan'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
